import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook used to detect clicks outside a component (or an escape key press).
 */
export function useDetectClickOutside({ onTriggered }) {
  const ref = useRef(null);

  const keyListener = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onTriggered(e);
      }
    },
    [onTriggered],
  );

  const clickOrTouchListener = useCallback(
    (e) => {
      if (ref && ref.current) {
        if (!(ref.current).contains(e.target)) {
          onTriggered?.(e);
        }
      }
    },
    [ref.current, onTriggered],
  );

  useEffect(() => {
    // Need to attach event listener to shadow root if exists.
    if (window.appRootMountPoint) {
      window.appRootMountPoint.addEventListener('click', clickOrTouchListener);
      window.appRootMountPoint.addEventListener('touch', clickOrTouchListener);
      window.appRootMountPoint.addEventListener('keyup', keyListener);
    }
    else {
      document.addEventListener('click', clickOrTouchListener);
      document.addEventListener('touch', clickOrTouchListener);
      document.addEventListener('keyup', keyListener);
    }
    return () => {
      if (window.appRootMountPoint) {
        window.appRootMountPoint.removeEventListener('click', clickOrTouchListener);
        window.appRootMountPoint.removeEventListener('touch', clickOrTouchListener);
        window.appRootMountPoint.removeEventListener('keyup', keyListener);
      }
      else {
        document.removeEventListener('click', clickOrTouchListener);
        document.removeEventListener('touch', clickOrTouchListener);
        document.removeEventListener('keyup', keyListener);
      }
    };
  }, []);
  return ref;
}
